/**
 * Record a move-in or move-out inspection: itemized rows, each with a
 * condition. MCL 554.608 requires two blank copies at move-in — this is the
 * inspection record, not the blank copies themselves, which are a physical
 * handoff outside the app.
 */

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useCreate, useOne } from '@refinedev/core';
import { CHECKLIST_CONDITIONS } from 'shared';
import { Field, FormErrors, Select, TextInput, fieldErrors } from '../../../components/Form.js';

interface ItemRow {
  area: string;
  item: string;
  condition: string;
  notes: string;
  estimatedCost: string;
}

const EMPTY_ITEM: ItemRow = { area: '', item: '', condition: 'GOOD', notes: '', estimatedCost: '' };

interface LeaseRecord {
  id: string;
  unitLabel: string;
  propertyName: string;
}

export function ChecklistForm() {
  const { leaseId } = useParams<{ leaseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const kind = searchParams.get('kind') === 'MOVE_OUT' ? 'MOVE_OUT' : 'MOVE_IN';

  const [inspectedOn, setInspectedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [tenantPresent, setTenantPresent] = useState(false);
  const [items, setItems] = useState<ItemRow[]>([{ ...EMPTY_ITEM }]);

  const { data: leaseData } = useOne<LeaseRecord>({
    resource: 'leases',
    id: leaseId ?? '',
    queryOptions: { enabled: Boolean(leaseId) },
  });

  const { mutateAsync, isLoading, error } = useCreate();

  function updateItem(index: number, patch: Partial<ItemRow>) {
    setItems((previous) => previous.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function addItem() {
    setItems((previous) => [...previous, { ...EMPTY_ITEM }]);
  }

  function removeItem(index: number) {
    setItems((previous) => previous.filter((_, i) => i !== index));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!leaseId) return;

    await mutateAsync({
      resource: 'checklists',
      values: {
        leaseId,
        kind,
        inspectedOn,
        tenantPresent,
        items: items.map((item) => ({
          area: item.area,
          item: item.item,
          condition: item.condition,
          notes: item.notes || null,
          estimatedCost: item.estimatedCost || null,
        })),
      },
    });

    void navigate(`/staff/leases/${leaseId}`);
  }

  const errors = fieldErrors(error);
  const lease = leaseData?.data;

  return (
    <section>
      <div className="row">
        <h1>
          {kind === 'MOVE_IN' ? 'Move-in checklist' : 'Move-out checklist'}
          {lease ? ` — ${lease.unitLabel}, ${lease.propertyName}` : ''}
        </h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate(`/staff/leases/${leaseId ?? ''}`)}
        >
          ← Back to lease
        </button>
      </div>

      {kind === 'MOVE_OUT' ? (
        <p className="alert alert--warn">
          Estimated cost on any item here feeds the itemized damages notice — MCL 554.609 requires
          it to be itemized, not a lump sum.
        </p>
      ) : null}

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={error} />

        <Field label="Inspected on" name="inspectedOn" required error={errors.inspectedOn}>
          <TextInput
            name="inspectedOn"
            type="date"
            value={inspectedOn}
            onChange={setInspectedOn}
            required
          />
        </Field>

        <label className="row">
          <input
            type="checkbox"
            checked={tenantPresent}
            onChange={(event) => setTenantPresent(event.target.checked)}
          />
          <span>Tenant was present for the inspection</span>
        </label>

        <h2>Items</h2>
        {items.map((item, index) => (
          <div key={index} className="card stack">
            <div className="row">
              <TextInput
                name={`area-${String(index)}`}
                placeholder="Area (e.g. Kitchen)"
                value={item.area}
                onChange={(v) => updateItem(index, { area: v })}
              />
              <TextInput
                name={`item-${String(index)}`}
                placeholder="Item (e.g. Countertop)"
                value={item.item}
                onChange={(v) => updateItem(index, { item: v })}
              />
              <Select
                name={`condition-${String(index)}`}
                value={item.condition}
                onChange={(v) => updateItem(index, { condition: v })}
                options={CHECKLIST_CONDITIONS}
              />
            </div>
            <div className="row">
              <TextInput
                name={`notes-${String(index)}`}
                placeholder="Notes"
                value={item.notes}
                onChange={(v) => updateItem(index, { notes: v })}
              />
              {kind === 'MOVE_OUT' ? (
                <TextInput
                  name={`cost-${String(index)}`}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Estimated cost"
                  value={item.estimatedCost}
                  onChange={(v) => updateItem(index, { estimatedCost: v })}
                />
              ) : null}
              {items.length > 1 ? (
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => removeItem(index)}
                >
                  Remove
                </button>
              ) : null}
            </div>
          </div>
        ))}

        <button type="button" className="button button--quiet" onClick={addItem}>
          Add item
        </button>

        <div className="row">
          <button type="submit" className="button" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save checklist'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void navigate(`/staff/leases/${leaseId ?? ''}`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
